import apiClient from "./axios";

/**
 * 航路管理API服务 - 符合IATA/ICAO标准
 */
const flightRouteApi = {
    // 标准CRUD操作 - 符合API_GUIDELINES.md规范
    /**
     * 获取航路列表 (标准方法名)
     * @param {Object} params - 查询参数
     * @returns {Promise}
     */
    getList: (params = {}) => {
        return apiClient.get("/v1/flight-routes", { params });
    },

    /**
     * 根据ID获取航路详情 (标准方法名)
     * @param {number} id - 航路ID
     * @returns {Promise}
     */
    getById: (id) => {
        return apiClient.get(`/v1/flight-routes/${id}`);
    },

    /**
     * 创建航路 (标准方法名)
     * @param {Object} data - 航路数据
     * @returns {Promise}
     */
    create: (data) => {
        return apiClient.post("/v1/flight-routes", data);
    },

    /**
     * 更新航路 - 符合API规范的对象参数模式 (标准方法名)
     * @param {Object} params - 参数对象
     * @param {number} params.id - 航路ID
     * @param {Object} params.data - 更新数据
     * @returns {Promise}
     */
    update: ({ id, data }) => {
        return apiClient.put(`/v1/flight-routes/${id}`, data);
    },

    /**
     * 删除航路 (标准方法名)
     * @param {number} id - 航路ID
     * @returns {Promise}
     */
    delete: (id) => {
        return apiClient.delete(`/v1/flight-routes/${id}`);
    },

    // 兼容性方法 - 保持现有调用不受影响
    /**
     * 获取航路列表 (兼容方法)
     * @param {Object} params - 查询参数
     * @returns {Promise}
     */
    getFlightRoutes: (params = {}) => {
        return apiClient.get("/v1/flight-routes", { params });
    },

    /**
     * 根据ID获取航路详情 (兼容方法)
     * @param {number} id - 航路ID
     * @returns {Promise}
     */
    getFlightRouteById: (id) => {
        return apiClient.get(`/v1/flight-routes/${id}`);
    },

    /**
     * 创建航路 (兼容方法)
     * @param {Object} data - 航路数据
     * @returns {Promise}
     */
    createFlightRoute: (data) => {
        return apiClient.post("/v1/flight-routes", data);
    },

    /**
     * 更新航路 (兼容方法) - 保持原有分离参数格式
     * @param {number} id - 航路ID
     * @param {Object} data - 更新数据
     * @returns {Promise}
     */
    updateFlightRoute: (id, data) => {
        return apiClient.put(`/v1/flight-routes/${id}`, data);
    },

    /**
     * 删除航路 (兼容方法)
     * @param {number} id - 航路ID
     * @returns {Promise}
     */
    deleteFlightRoute: (id) => {
        return apiClient.delete(`/v1/flight-routes/${id}`);
    },

    /**
     * 批量操作航路
     * @param {Object} data - 批量操作数据
     * @returns {Promise}
     */
    batchOperation: (data) => {
        return apiClient.post("/v1/flight-routes/batch", data);
    },

    /**
     * 按航路ID批量获取航路详情
     * @param {number[]} ids - 航路ID列表
     * @returns {Promise}
     */
    getByIds: (ids) => {
        return apiClient.post("/v1/flight-routes/batch-get-by-ids", { ids });
    },

    /**
     * 设置默认航路
     * @param {Object} data - 设置默认航路数据
     * @returns {Promise}
     */
    setDefaultRoute: (data) => {
        return apiClient.post("/v1/flight-routes/set-default", data);
    },

    /**
     * 根据航段获取航路列表
     * @param {string} departureAirport - 出发机场
     * @param {string} arrivalAirport - 到达机场
     * @param {boolean|undefined} isActive - 是否过滤航路状态：true=仅有效，false=仅失效，undefined/不传=所有航路（默认）
     * @returns {Promise}
     */
    getRoutesByLeg: (departureAirport, arrivalAirport, isActive) => {
        // 【航段航路BH优化】
        // - 不传isActive参数（undefined）→ 返回所有航路（有效+失效），由调用方自行筛选（默认行为）
        // - isActive=true → 只返回有效航路
        // - isActive=false → 只返回失效航路
        const params = isActive !== undefined ? { is_active: isActive } : {};
        return apiClient.get(`/v1/flight-routes/by-leg/${departureAirport}/${arrivalAirport}`, {
            params,
        });
    },

    /**
     * 根据航段和飞机获取适配的航路列表
     * @param {string} departureAirport - 出发机场
     * @param {string} arrivalAirport - 到达机场
     * @param {string} aircraftReg - 飞机注册号（可选）
     * @returns {Promise}
     */
    getCompatibleRoutes: (departureAirport, arrivalAirport, aircraftReg = null) => {
        const params = aircraftReg ? { aircraft_reg: aircraftReg } : {};
        return apiClient.get(`/v1/flight-routes/compatible/${departureAirport}/${arrivalAirport}`, { params });
    },

    /**
     * 更新航路图片
     * @param {number} routeId - 航路ID
     * @param {string} imageUrl - 图片URL
     * @returns {Promise}
     */
    updateRouteImage: (routeId, imageUrl) => {
        return apiClient.put(`/v1/flight-routes/${routeId}/image`, {
            route_id: routeId,
            image_url: imageUrl,
        });
    },

    /**
     * 更新航路标签
     * @param {number} routeId - 航路ID
     * @param {Array} tags - 标签列表
     * @returns {Promise}
     */
    updateRouteTags: (routeId, tags) => {
        return apiClient.put(`/v1/flight-routes/${routeId}/tags`, {
            route_id: routeId,
            tags: tags,
        });
    },

    /**
     * 获取所有航路标签
     * @returns {Promise}
     */
    getAllTags: () => {
        return apiClient.get("/v1/flight-routes/tags");
    },

    /**
     * 获取最佳匹配的航路（单个结果）
     * 选择顺序（与后端同一套）：这架飞机能用的默认航路 > 适配这架飞机的航路 > 这架飞机能用的其他航路；
     * 一条能用的都没有时返回 null，不退回绑定不适配这架飞机的默认航路
     * @param {string} departureAirport - 出发机场
     * @param {string} arrivalAirport - 到达机场
     * @param {string} aircraftReg - 飞机注册号（可选）
     * @returns {Promise}
     */
    getBestMatchRoute: (departureAirport, arrivalAirport, aircraftReg = null) => {
        const params = aircraftReg ? { aircraft_reg: aircraftReg } : {};
        return apiClient.get(`/v1/flight-routes/best-match/${departureAirport}/${arrivalAirport}`, { params });
    },

    /**
     * 批量获取多个航段的最佳匹配航路
     * @param {Object} data - 请求体
     * @param {string} data.aircraft_reg - 飞机注册号
     * @param {Array<Object>} data.legs - 航段列表 [{departure_airport, arrival_airport}]
     * @returns {Promise}
     */
    getBulkBestMatchRoutes: (data) => {
        return apiClient.post("/v1/flight-routes/best-match/bulk", data);
    },

    /**
     * 获取航路图片的签名 URL
     * @param {number} routeId - 航路ID
     * @param {boolean} thumbnail - 是否返回缩略图（默认 false 返回原图）
     * @returns {Promise}
     */
    getRouteImageUrl: (routeId, thumbnail = false) => {
        return apiClient.get(`/v1/flight-routes/${routeId}/image-url`, {
            params: { thumbnail },
        });
    },

    // ── 航路组合查询 ──────────────────────────────────────

    getRouteCombinations: (origin, destination) => {
        return apiClient.get("/v1/flight-routes/combinations", {
            params: { origin, destination },
        });
    },
};

export { flightRouteApi };
